import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { postsAPI } from '../../services/api';
import { Post } from '../../types';
import toast from 'react-hot-toast';

interface PostsState {
  posts: Post[];
  feedPosts: Post[];
  userPosts: Post[];
  currentPost: Post | null;
  loading: boolean;
  feedLoading: boolean;
  createLoading: boolean;
  likeLoading: { [postId: string]: boolean };
  commentLoading: { [postId: string]: boolean };
  currentPage: number;
  hasMore: boolean;
  error: string | null;
}

const initialState: PostsState = {
  posts: [],
  feedPosts: [],
  userPosts: [],
  currentPost: null,
  loading: false,
  feedLoading: false,
  createLoading: false,
  likeLoading: {},
  commentLoading: {},
  currentPage: 1,
  hasMore: true,
  error: null,
};

// Async thunks
export const fetchFeed = createAsyncThunk(
  'posts/fetchFeed',
  async ({ page = 1, refresh = false }: { page?: number; refresh?: boolean }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.getFeed(page);
      return {
        posts: response.data.data.posts,
        page,
        refresh,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch feed');
    }
  }
);

export const createPost = createAsyncThunk(
  'posts/createPost',
  async (postData: {
    content: string;
    mediaUrls: string[];
    isCollaboration: boolean;
    collaborationPartners: string[];
    tags: string[];
  }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.create(postData);
      return response.data.data.post;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create post');
    }
  }
);

export const toggleLike = createAsyncThunk(
  'posts/toggleLike',
  async (postId: string, { rejectWithValue }) => {
    try {
      const response = await postsAPI.like(postId);
      return {
        postId,
        liked: response.data.data.liked,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle like');
    }
  }
);

export const addComment = createAsyncThunk(
  'posts/addComment',
  async ({ postId, content }: { postId: string; content: string }, { rejectWithValue }) => {
    try {
      const response = await postsAPI.addComment(postId, { content });
      return {
        postId,
        comment: response.data.data.comment,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add comment');
    }
  }
);

export const fetchComments = createAsyncThunk(
  'posts/fetchComments',
  async (postId: string, { rejectWithValue }) => {
    try {
      const response = await postsAPI.getComments(postId);
      return {
        postId,
        comments: response.data.data.comments,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch comments');
    }
  }
);

export const fetchUserPosts = createAsyncThunk(
  'posts/fetchUserPosts',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Assuming you have an endpoint for user-specific posts
      const response = await postsAPI.getUserPosts(userId);
      return response.data.data.posts;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user posts');
    }
  }
);

export const deletePost = createAsyncThunk(
  'posts/deletePost',
  async (postId: string, { rejectWithValue }) => {
    try {
      await postsAPI.deletePost(postId);
      return postId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete post');
    }
  }
);

export const sharePost = createAsyncThunk(
  'posts/sharePost',
  async (postId: string, { rejectWithValue }) => {
    try {
      const response = await postsAPI.sharePost(postId);
      return {
        postId,
        shareCount: response.data.data.share_count,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to share post');
    }
  }
);

const postsSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    clearPosts: (state) => {
      state.posts = [];
      state.feedPosts = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
    setCurrentPost: (state, action: PayloadAction<Post>) => {
      state.currentPost = action.payload;
    },
    clearCurrentPost: (state) => {
      state.currentPost = null;
    },
    updatePostInState: (state, action: PayloadAction<Partial<Post> & { id: string }>) => {
      const { id, ...updates } = action.payload;
      
      // Update in all relevant arrays
      const updatePost = (post: Post) => {
        if (post.id === id) {
          return { ...post, ...updates };
        }
        return post;
      };
      
      state.posts = state.posts.map(updatePost);
      state.feedPosts = state.feedPosts.map(updatePost);
      state.userPosts = state.userPosts.map(updatePost);
      
      if (state.currentPost?.id === id) {
        state.currentPost = { ...state.currentPost, ...updates };
      }
    },
    addNewPost: (state, action: PayloadAction<Post>) => {
      // Add new post to the beginning of feeds
      state.feedPosts.unshift(action.payload);
      state.posts.unshift(action.payload);
    },
    removePost: (state, action: PayloadAction<string>) => {
      const postId = action.payload;
      state.posts = state.posts.filter(post => post.id !== postId);
      state.feedPosts = state.feedPosts.filter(post => post.id !== postId);
      state.userPosts = state.userPosts.filter(post => post.id !== postId);
      
      if (state.currentPost?.id === postId) {
        state.currentPost = null;
      }
    },
    resetFeed: (state) => {
      state.feedPosts = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch feed
      .addCase(fetchFeed.pending, (state) => {
        state.feedLoading = true;
        state.error = null;
      })
      .addCase(fetchFeed.fulfilled, (state, action) => {
        state.feedLoading = false;
        
        // Safe destructuring with defaults
        const { 
          posts = [], 
          page = 1, 
          refresh = false 
        } = action.payload || {};
        
        // Ensure posts is always an array
        const postsArray = Array.isArray(posts) ? posts : [];
        
        if (refresh || page === 1) {
          // Replace feed for refresh or first page
          state.feedPosts = postsArray;
          state.posts = postsArray;
          state.currentPage = 1;
        } else {
          // Append posts for pagination
          const currentFeed = Array.isArray(state.feedPosts) ? state.feedPosts : [];
          const currentPosts = Array.isArray(state.posts) ? state.posts : [];
          
          state.feedPosts = [...currentFeed, ...postsArray];
          state.posts = [...currentPosts, ...postsArray];
        }
        
        state.currentPage = page;
        state.hasMore = postsArray.length > 0;
      })
      .addCase(fetchFeed.rejected, (state, action) => {
        state.feedLoading = false;
        state.error = action.payload as string;
        toast.error('Failed to load feed');
      })
      
      // Create post
      .addCase(createPost.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.createLoading = false;
        // Add new post to beginning of feeds
        state.feedPosts.unshift(action.payload);
        state.posts.unshift(action.payload);
        state.userPosts.unshift(action.payload);
        toast.success('Post created successfully!');
      })
      .addCase(createPost.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
        toast.error('Failed to create post');
      })
      
      // Toggle like
      .addCase(toggleLike.pending, (state, action) => {
        const postId = action.meta.arg;
        state.likeLoading[postId] = true;
      })
      .addCase(toggleLike.fulfilled, (state, action) => {
        const { postId, liked } = action.payload;
        state.likeLoading[postId] = false;
        
        // Update like status and count in all relevant arrays
        const updateLike = (post: Post) => {
          if (post.id === postId) {
            return {
              ...post,
              hasLiked: liked,
              likeCount: liked ? post.likeCount + 1 : post.likeCount - 1,
            };
          }
          return post;
        };
        
        state.posts = state.posts.map(updateLike);
        state.feedPosts = state.feedPosts.map(updateLike);
        state.userPosts = state.userPosts.map(updateLike);
        
        if (state.currentPost?.id === postId) {
          state.currentPost = updateLike(state.currentPost);
        }
      })
      .addCase(toggleLike.rejected, (state, action) => {
        const postId = action.meta.arg;
        state.likeLoading[postId] = false;
        state.error = action.payload as string;
        toast.error('Failed to update like');
      })
      
      // Add comment
      .addCase(addComment.pending, (state, action) => {
        const { postId } = action.meta.arg;
        state.commentLoading[postId] = true;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { postId } = action.payload;
        state.commentLoading[postId] = false;
        
        // Update comment count
        const updateCommentCount = (post: Post) => {
          if (post.id === postId) {
            return { ...post, commentCount: post.commentCount + 1 };
          }
          return post;
        };
        
        state.posts = state.posts.map(updateCommentCount);
        state.feedPosts = state.feedPosts.map(updateCommentCount);
        state.userPosts = state.userPosts.map(updateCommentCount);
        
        if (state.currentPost?.id === postId) {
          state.currentPost = updateCommentCount(state.currentPost);
        }
        
        toast.success('Comment added!');
      })
      .addCase(addComment.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        state.commentLoading[postId] = false;
        state.error = action.payload as string;
        toast.error('Failed to add comment');
      })
      
      // Fetch user posts
      .addCase(fetchUserPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.userPosts = action.payload;
      })
      .addCase(fetchUserPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load user posts');
      })
      
      // Delete post
      .addCase(deletePost.fulfilled, (state, action) => {
        const postId = action.payload;
        state.posts = state.posts.filter(post => post.id !== postId);
        state.feedPosts = state.feedPosts.filter(post => post.id !== postId);
        state.userPosts = state.userPosts.filter(post => post.id !== postId);
        
        if (state.currentPost?.id === postId) {
          state.currentPost = null;
        }
        
        toast.success('Post deleted successfully');
      })
      .addCase(deletePost.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to delete post');
      })
      
      // Share post
      .addCase(sharePost.fulfilled, (state, action) => {
        const { postId, shareCount } = action.payload;
        
        const updateShareCount = (post: Post) => {
          if (post.id === postId) {
            return { ...post, shareCount };
          }
          return post;
        };
        
        state.posts = state.posts.map(updateShareCount);
        state.feedPosts = state.feedPosts.map(updateShareCount);
        state.userPosts = state.userPosts.map(updateShareCount);
        
        if (state.currentPost?.id === postId) {
          state.currentPost = updateShareCount(state.currentPost);
        }
        
        toast.success('Post shared!');
      })
      .addCase(sharePost.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to share post');
      });
  },
});

export const {
  clearPosts,
  setCurrentPost,
  clearCurrentPost,
  updatePostInState,
  addNewPost,
  removePost,
  resetFeed,
  clearError,
} = postsSlice.actions;

export default postsSlice.reducer;
